import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiUboShow, apiUboUpdate, Ubo } from "../lib/api";
const incomeOptions = [
  ["salary", "Salary / employment"],
  ["business_income", "Business income"],
  ["investment_income", "Investment income"],
  ["savings", "Savings"],
  ["pension", "Pension"],
  ["inheritance", "Inheritance"],
  ["property_sale", "Property sale"],
  ["other", "Other"],
];

function renderLivenessBanner(ubo: Ubo | null) {
  if (!ubo || !ubo.kyc_liveness) return null;
  const snap: any = ubo.kyc_liveness;
  const ok = !!(snap.ok ?? snap.pass);
  const label: string = (snap.label ||
    snap.selfie_label ||
    snap.mrz_label ||
    (ok ? "PASS" : "CHECK")) as string;
  const spoof =
    typeof snap.spoof_score === "number"
      ? (snap.spoof_score as number)
      : undefined;

  const cls = ok ? "alert-success" : "alert-danger";
  const title = ok ? "UBO KYC / Liveness passing" : "UBO KYC / Liveness not passing";

  const lines: string[] = [`Decision: ${label}`];
  if (spoof !== undefined) {
    lines.push(`Spoof score: ${spoof.toFixed(2)}`);
  }

  return (
    <div className={`${cls} mb-4`}>
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-xs mt-1">{lines.join(" · ")}</div>
    </div>
  );
}

export default function UboEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ubo, setUbo] = useState<Ubo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isPep, setIsPep] = useState("");
  const [sourceOfIncome, setSourceOfIncome] = useState("");

  useEffect(() => {
    (async () => {
      try {
        if (!id) return;
        const data = await apiUboShow(Number(id));
        setUbo(data);
        setIsPep(data.is_pep ? "1" : "0");
        setSourceOfIncome((data as any).source_of_income || "");
      } catch (e: any) {
        setErr(e.message || "Failed to load UBO");
      }
    })();
  }, [id]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!id) return;
    setErr(null);
    setSaving(true);
    const fd = new FormData(e.currentTarget);

    try {
      await apiUboUpdate(Number(id), fd);
      // FIX: go back to SPA UBO index under /profile/ubos
      nav("/profile/ubos");
    } catch (e: any) {
      setErr(e.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  }

  if (!ubo) return <div className="p-4">{err || "Loading…"}</div>;

  return (
    <div className="max-w-2xl mx-auto bg-white rounded shadow p-4">
      <h1 className="text-xl font-bold mb-4">Edit UBO</h1>

      <p className="text-sm text-gray-600 mb-4">
        Update the UBO’s identity details here. The UBO’s documents (ID, selfie,
        proof of address) are handled entirely through their own KYC flow – you no
        longer upload files for them from this page.
      </p>

      {err && (
        <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{err}</div>
      )}

      {renderLivenessBanner(ubo)}

      {ubo.kyc_link && (
        <div className="mb-4">
          <a
            href={ubo.kyc_link}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-white"
          >
            Open UBO KYC
          </a>
          <p className="text-xs text-gray-500 mt-1">
            Share this link with the UBO if they need to redo their KYC (ID / selfie
            / proof of address).
          </p>
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label font-semibold">First name:</label>
            <input
              name="first_name"
              defaultValue={ubo.first_name}
              className="input"
              required
            />
          </div>
          <div>
            <label className="label font-semibold">Last name:</label>
            <input
              name="last_name"
              defaultValue={ubo.last_name}
              className="input"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block font-semibold mb-1">
              Residential address:
            </label>
            <input
              name="residential_address"
              defaultValue={ubo.residential_address}
              className="input w-full"
              required
            />
          </div>

          <div>
            <label className="label font-semibold">Nationality:</label>
            <input
              name="nationality"
              defaultValue={ubo.nationality}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Birthdate</label>
            <input
              type="date"
              name="birthdate"
              defaultValue={ubo.birthdate as any}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Ownership %</label>
            <input
              name="ownership_percent"
              type="number"
              min={0}
              max={100}
              step="0.01"
              defaultValue={ubo.ownership_percent}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Politically exposed person (PEP)</label>
            <select
              name="is_pep"
              value={isPep}
              onChange={(e) => setIsPep(e.target.value)}
              className="input"
              required
            >
              <option value="">Select...</option>
              <option value="0">No</option>
              <option value="1">Yes</option>
            </select>
          </div>

          <div>
            <label className="label">Source of income</label>
            <select
              name="source_of_income"
              value={sourceOfIncome}
              onChange={(e) => setSourceOfIncome(e.target.value)}
              className="input"
              required
            >
              <option value="">Select...</option>
              {incomeOptions.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {isPep === "1" && (
            <div className="md:col-span-2">
              <label className="label font-semibold">PEP details:</label>
              <textarea
                name="pep_details"
                defaultValue={(ubo as any).pep_details}
                className="input w-full"
                rows={3}
                maxLength={1000}
                required
              />
            </div>
          )}

          {sourceOfIncome === "other" && (
            <div className="md:col-span-2">
              <label className="label font-semibold">Other source of income:</label>
              <input
                name="source_of_income_other"
                defaultValue={(ubo as any).source_of_income_other}
                className="input w-full"
                maxLength={255}
                required
              />
            </div>
          )}

          {/* Optional: email field if your backend supports editing it */}
          <div className="md:col-span-2">
            <label className="label font-semibold">
              UBO Email (for KYC invites):
            </label>
            <input
              type="email"
              name="email"
              defaultValue={(ubo as any).email}
              className="input w-full"
              placeholder="ubo@example.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              If you change this email, future KYC invites will be sent there.
            </p>
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          <button className="btn" disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </button>
          <button
            type="button"
            onClick={() => nav(-1)}
            className="btn btn-white"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
